import { RoomType } from "../../types/floorplannerTypes";
import { formatArea } from "../../utils/conversions";
import { editorStore } from "../../store/editorStore";
import React, { useMemo } from "react";
import { observer } from "mobx-react-lite";
import { SimpleText } from "../Text/SimpleText";
import { renderStore } from "../../store/renderStore";

export const RoomLabel: React.FC<{ room: RoomType }> = observer(({ room }) => {
  // Check if measurements should be shown based on zoom level
  const shouldShowMeasurements = editorStore.shouldShowMeasurements();
  
  // Calculate zoom-derived values directly (same as MeasurementLine) to ensure consistency
  // Accessing these functions will automatically track MobX observables via observer()
  const measurementScale = editorStore.getMeasurementVisibilityScale();
  const zoomLevelDivisor = editorStore.measurementZoomLevelDivisor();
  
  // Calculate values directly on each render (same pattern as MeasurementLine)
  const textFontSize = (14 / zoomLevelDivisor) * measurementScale;
  const textBoxPadding = (0.13 / zoomLevelDivisor) * measurementScale;
  const textBoxHeight = (0.16 / zoomLevelDivisor) * measurementScale;
  const textBoxWidth = (0.6 / zoomLevelDivisor) * measurementScale;

  // Calculate offset for group translation
  const offset = useMemo(() => {
    if (!renderStore.isGroupTranslating) return { x: 0, y: 0 };

    // Check if all walls of this room are being translated
    // We only move the label if the entire room (all walls) is being moved
    const allWallsTranslating = room.objectIds.length > 0 && room.objectIds.every(wallId =>
      renderStore.translatingWallIds.has(wallId)
    );

    if (allWallsTranslating) {
      return renderStore.groupTranslateOffset;
    }
    return { x: 0, y: 0 };
  }, [renderStore.isGroupTranslating, renderStore.groupTranslateOffset, room.objectIds]);

  // Calculate rotation offset for group rotation
  const rotationData = useMemo(() => {
    if (!renderStore.isGroupRotating) return null;

    // Check if all walls of this room are being rotated
    // We only rotate the label if the entire room (all walls) is being rotated
    const allWallsRotating = room.objectIds.length > 0 && room.objectIds.every(wallId =>
      renderStore.rotatingWallIds.has(wallId)
    );

    if (allWallsRotating) {
      return { angle: renderStore.groupRotationAngle, center: renderStore.groupRotationCenter };
    }
    return null;
  }, [renderStore.isGroupRotating, renderStore.groupRotationAngle, renderStore.groupRotationCenter, room.objectIds]);

  // Calculate final position with both translate and rotation offsets
  const finalPosition = useMemo(() => {
    let pos = { x: room.centroid.x + offset.x, y: room.centroid.y + offset.y };
    if (rotationData) {
      const { angle, center } = rotationData;
      const dx = pos.x - center.x;
      const dy = pos.y - center.y;
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      pos = {
        x: center.x + dx * cos - dy * sin,
        y: center.y + dx * sin + dy * cos
      };
    }
    return pos;
  }, [room.centroid.x, room.centroid.y, offset.x, offset.y, rotationData]);

  // Don't render anything if measurements should be hidden
  if (!shouldShowMeasurements) {
    return <group />;
  }

  // Don't render room labels with very small area (< 0.1 m² in world space)
  // This can happen for degenerate polygons (like multi-joints that aren't real rooms)
  if (!Number.isFinite(room.areaWorldUnits) || room.areaWorldUnits < 0.1) {
    return <group />;
  }

  return (
    <group position={[finalPosition.x, finalPosition.y, 0.01]}>
      <mesh position={[0, 0, -0.01]}>
        <planeGeometry
          args={[
            textBoxWidth + textBoxPadding,
            textBoxHeight,
          ]}
        />
        <meshBasicMaterial color={"white"} />
      </mesh>
      <SimpleText
        position={[0, 0, 0]}
        fontSize={textFontSize}
        color="#222222"
      >
        {formatArea(room.areaWorldUnits, editorStore.displayLengthUnit)}
      </SimpleText>
    </group>
  );
});
